import "./CampaignMappingTool.scss";
import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps, Router } from "@reach/router";
import { MdSmsFailed } from "react-icons/md";
import { Page } from "../Components/Page";
import { FullPageSpinner } from "../Components/Spinner";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import { SubBrand1 } from "../utils/colors";
import {
  fetchAndParseCachedCsv,
  fetchCometLambda,
  SegmentationMappingLambdaFetch,
} from "../utils/fetch-utils";
import { useNavigationGuard } from "../utils/hooks/useNavigationGuard";
import { useTabbedNav } from "../utils/hooks/useNav";
import CampaignMappingLabeling from "./CampaignMappingLabeling";
import {
  MAPPING_LEVELS,
  normalizeCampaignMappingRow,
  toMappingOptions,
} from "./CampaignMappingUtils";

import {
  CampaignMappingRow,
  MappingLevel,
  MappingOption,
  CampaignOptionsResponse,
} from "@blisspointmedia/bpm-types/dist/CampaignMappingTool";

const emptyMappingOptions = (): Record<MappingLevel, MappingOption[]> =>
  MAPPING_LEVELS.reduce((acc, level) => {
    acc[level] = [];
    return acc;
  }, {} as Record<MappingLevel, MappingOption[]>);

const RAW_CAMPAIGN_DATA_COMET_LAMBDA = "segmentationmapping-getRawCampaignData";
const CAMPAIGN_OPTIONS_COMET_LAMBDA = "segmentationmapping-getCampaignOptions";

interface CampaignOptionsParams {
  company: string;
}

const enum PageTab {
  LABELING = "labeling",
}

const NAVS = [{ label: "Labeling", key: PageTab.LABELING }];

const CampaignMappingTool: React.FC<RouteComponentProps> = ({ navigate }) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState<CampaignMappingRow[] | null>(null);
  const [mappingOptions, setMappingOptions] = useState<Record<MappingLevel, MappingOption[]>>(
    emptyMappingOptions
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "campaign-mapping-tool",
    defaultKey: PageTab.LABELING,
    navs: NAVS.map(nav => nav.key, NAVS),
  });

  useEffect(() => {
    if (!tab) {
      goToTab(PageTab.LABELING);
    }
  }, [goToTab, tab]);

  const {
    attemptNavigate: handleTabClick,
    confirmNavigation: confirmTabNavigation,
    cancelNavigation: cancelTabNavigation,
    showAlert: showAlertModal,
  } = useNavigationGuard({
    hasUnsavedChanges,
    onConfirmNavigate: key => {
      goToTab(key);
      setHasUnsavedChanges(false);
    },
  });

  const fetchRawData = useCallback(async () => {
    try {
      setLoading(true);

      const params: CampaignOptionsParams = {
        company: cid,
      };

      const [campaignOptionsCacheKey, rawDataCacheKey] = await Promise.all([
        fetchCometLambda<CampaignOptionsParams, string>({
          service: SegmentationMappingLambdaFetch,
          standardEndpoint: "/getCampaignOptions",
          cometLambdaName: CAMPAIGN_OPTIONS_COMET_LAMBDA,
          input: params,
        }),
        fetchCometLambda<CampaignOptionsParams, string>({
          service: SegmentationMappingLambdaFetch,
          standardEndpoint: "/getRawCampaignData",
          cometLambdaName: RAW_CAMPAIGN_DATA_COMET_LAMBDA,
          input: params,
        }),
      ]);

      const campaignOptions = await fetchAndParseCachedCsv<CampaignOptionsResponse>(
        campaignOptionsCacheKey
      );
      setMappingOptions(toMappingOptions(campaignOptions));

      const parsedRows = await fetchAndParseCachedCsv<Record<string, unknown>>(rawDataCacheKey);
      const rows = parsedRows.map(normalizeCampaignMappingRow).filter(row => Boolean(row.raw_data_id));

      setRawData(rows);
    } catch (error) {
      const reportError = error as Error;
      setError({ message: reportError.message, reportError });
    } finally {
      setLoading(false);
    }
  }, [cid, setError]);

  useEffect(() => {
    if (cid) {
      setRawData(null);
      setMappingOptions(emptyMappingOptions());
      setHasUnsavedChanges(false);
    }
  }, [cid]);

  useEffect(() => {
    if (!rawData && cid) {
      fetchRawData();
    }
  }, [cid, fetchRawData, rawData]);

  return (
    <Page
      app2Redesign
      title="Campaign Mapping Tool"
      pageType="Campaign Mapping Tool"
      navs={NAVS}
      selectedNav={tab}
      onNav={handleTabClick}
    >
      <div className="campaignMappingToolPage">
        {loading && <FullPageSpinner />}
        <Router className="fullPageRouter">
          <CampaignMappingLabeling
            path={PageTab.LABELING}
            data={rawData}
            mappingOptions={mappingOptions}
            fetchRawData={fetchRawData}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </Router>
      </div>
      <AlertDialog
        title="Are you sure you want to leave?"
        body="If you leave now, your changes will not be saved."
        icon={<MdSmsFailed color={SubBrand1} size={75} />}
        variant={AlertDialogVariants.ERROR}
        show={showAlertModal}
        primaryButtonLabel="Stay"
        secondaryButtonLabel="Leave"
        onHide={cancelTabNavigation}
        secondaryAction={confirmTabNavigation}
      />
    </Page>
  );
};

export default CampaignMappingTool;
